/**
 * Macro-zone del corpo per il BodyMap parametrizzato.
 *
 * Le option dei form_field nel DB sono macro-zone testuali (es. "Braccia",
 * "Glutei", "Cosce") usate come valori di selectedZones. Qui mappiamo ogni
 * macro-label a un path SVG che disegna l'area corrispondente sull'outline
 * del corpo (riusando la geometria di BodyMap.tsx, viewBox 0 0 200 440).
 *
 * Pattern multi-subpath SVG: `M..Z M..Z` permette a un path di disegnare
 * piu' aree disgiunte (es. "Braccia" = entrambe le braccia in un unico
 * elemento cliccabile).
 *
 * `variant` indica su quale outline e' visibile la zona (front/back/face).
 * Il BodyMapField filtra le zone in base alla variant attiva.
 */

import type { ZoneDefinition } from './BodyMap';

export type MacroVariant = 'body-front' | 'body-back' | 'face';

export interface MacroZone extends ZoneDefinition {
  variant: MacroVariant;
}

/**
 * Mappa label-macro -> definizione zona. La label e' sia l'id sia
 * il valore salvato in form_answers (per coerenza con le option del DB).
 */
export const MACRO_ZONES: Record<string, MacroZone> = {
  // ---- Vista frontale ----
  'Viso': {
    id: 'Viso',
    label: 'Viso',
    variant: 'body-front',
    path: 'M88,18 C88,8 92,2 100,2 C108,2 112,8 112,18 L112,34 C112,42 108,48 100,48 C92,48 88,42 88,34 Z',
  },
  'Collo': {
    id: 'Collo',
    label: 'Collo',
    variant: 'body-front',
    path: 'M94,48 L106,48 L108,66 L92,66 Z',
  },
  'Spalle': {
    id: 'Spalle',
    label: 'Spalle',
    variant: 'body-front',
    path: 'M108,66 L130,70 L134,82 L118,86 L108,78 Z M92,66 L70,70 L66,82 L82,86 L92,78 Z',
  },
  'Petto': {
    id: 'Petto',
    label: 'Petto',
    variant: 'body-front',
    path: 'M82,78 L118,78 L120,118 L80,118 Z',
  },
  'Seno': {
    id: 'Seno',
    label: 'Seno',
    variant: 'body-front',
    path: 'M82,82 L100,80 L100,116 L82,116 Z M118,82 L100,80 L100,116 L118,116 Z',
  },
  'Torace': {
    id: 'Torace',
    label: 'Torace',
    variant: 'body-front',
    path: 'M82,78 L118,78 L120,118 L80,118 Z',
  },
  'Braccia': {
    id: 'Braccia',
    label: 'Braccia',
    variant: 'body-front',
    // Braccio + avambraccio dx + sx, in un singolo path multi-subpath
    path:
      'M134,82 L142,84 L146,140 L150,200 L140,202 L136,142 L130,118 L118,86 Z ' +
      'M66,82 L58,84 L54,140 L50,200 L60,202 L64,142 L70,118 L82,86 Z',
  },
  'Gomiti': {
    id: 'Gomiti',
    label: 'Gomiti',
    variant: 'body-front',
    // Area di transizione braccio/avambraccio (zona gomito approssimata)
    path:
      'M133,135 L148,133 L150,150 L135,152 Z ' +
      'M67,135 L52,133 L50,150 L65,152 Z',
  },
  'Addome': {
    id: 'Addome',
    label: 'Addome',
    variant: 'body-front',
    path: 'M80,118 L120,118 L122,170 L78,170 Z',
  },
  'Tronco': {
    id: 'Tronco',
    label: 'Tronco',
    variant: 'body-front',
    // Petto + addome
    path: 'M82,78 L118,78 L122,170 L78,170 Z',
  },
  'Cosce': {
    id: 'Cosce',
    label: 'Cosce',
    variant: 'body-front',
    path:
      'M112,200 L132,170 L134,270 L114,270 L108,240 Z ' +
      'M88,200 L68,170 L66,270 L86,270 L92,240 Z',
  },
  'Interno coscia': {
    id: 'Interno coscia',
    label: 'Interno coscia',
    variant: 'body-front',
    // Strisce interne delle cosce, vicino al centro del corpo
    path:
      'M108,205 L100,205 L100,265 L114,265 L112,240 Z ' +
      'M92,205 L100,205 L100,265 L86,265 L88,240 Z',
  },
  'Ginocchia': {
    id: 'Ginocchia',
    label: 'Ginocchia',
    variant: 'body-front',
    path:
      'M114,270 L134,270 L132,300 L112,300 Z ' +
      'M86,270 L66,270 L68,300 L88,300 Z',
  },
  'Gambe': {
    id: 'Gambe',
    label: 'Gambe',
    variant: 'body-front',
    // Cosce + polpacci (gambe complete frontali)
    path:
      'M112,200 L132,170 L134,270 L132,300 L128,390 L114,390 L112,300 L114,270 L108,240 Z ' +
      'M88,200 L68,170 L66,270 L68,300 L72,390 L86,390 L88,300 L86,270 L92,240 Z',
  },
  'Piedi': {
    id: 'Piedi',
    label: 'Piedi',
    variant: 'body-front',
    path:
      'M114,390 L128,390 L126,420 L134,430 L110,430 L112,420 Z ' +
      'M86,390 L72,390 L74,420 L66,430 L90,430 L88,420 Z',
  },

  // ---- Vista posteriore ----
  'Schiena': {
    id: 'Schiena',
    label: 'Schiena',
    variant: 'body-back',
    // Schiena alta + bassa
    path: 'M80,80 L120,80 L124,185 L76,185 Z',
  },
  'Glutei': {
    id: 'Glutei',
    label: 'Glutei',
    variant: 'body-back',
    path:
      'M100,185 L130,185 L132,220 L112,230 L100,220 Z ' +
      'M100,185 L70,185 L68,220 L88,230 L100,220 Z',
  },
};

/**
 * Dato l'array di label macro (= form_field.options), ritorna le ZoneDefinition
 * filtrate per variant. Le label non riconosciute sono ignorate (logghiamo
 * un warn in console per facilitare il debug se l'admin aggiunge una option
 * nuova senza definirla qui).
 */
export function buildZonesForOptions(
  options: string[],
  variant: MacroVariant,
): ZoneDefinition[] {
  return options
    .map((opt) => {
      const macro = MACRO_ZONES[opt];
      if (!macro) {
        console.warn(`[BodyMap] Zona macro non definita: "${opt}"`);
        return null;
      }
      if (macro.variant !== variant) return null;
      return { id: macro.id, label: macro.label, path: macro.path };
    })
    .filter((z): z is ZoneDefinition => z !== null);
}

/**
 * Determina quali variant sono necessarie per coprire tutte le option date.
 * Es. ["Braccia", "Glutei"] -> ['body-front', 'body-back'] perche' Braccia
 * sta sul front e Glutei sul back.
 */
export function getRequiredVariants(options: string[]): MacroVariant[] {
  const set = new Set<MacroVariant>();
  for (const opt of options) {
    const macro = MACRO_ZONES[opt];
    if (macro) set.add(macro.variant);
  }
  return Array.from(set);
}
